import type { BlockDefinition, InstagramBioProps } from '../types';
import { escapeHtml, safeUrl } from '../html';

/**
 * Instagram-style profile header — composite block that mimics IG's
 * actual bio layout. Customer fills each field manually (IG's TOS doesn't
 * permit scraping their public bio data, and 18+ accounts often don't
 * return data anyway); we give them the visual shell so they get the
 * familiar look in seconds.
 *
 * Layout (mobile-first, same as IG mobile):
 *
 *   [avatar w/ ring]   @username ✓                 ⋯
 *                      Display Name
 *
 *                      197         807K        97
 *                      posts       followers   following
 *
 *                      Creator
 *                      Multi-line bio body…
 *                      🔗 bouncy.ai/naya
 *
 * Inline blue verified checkmark (not on the avatar — IG never puts it
 * there). Story ring is the colorful conic gradient that IG uses when an
 * account has a new story.
 */

const STORY_RING_GRADIENT =
  'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)';

// Inline verified rosette. ViewBox is tightened to "0 0 24 16.28" so the
// rosette fills it cleanly (the path's y-range is 1.5–14.78, so the badge
// is centered at y≈8.14). The check path is positioned around (10.7, 8.5)
// — the OPTICAL center of the rosette — so the white tick sits squarely
// inside the blue body instead of drifting low-right.
const INLINE_VERIFIED_SVG = `<svg viewBox="0 0 24 16.28" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style="display:inline-block;width:18px;height:auto;vertical-align:-3px;flex-shrink:0;">
  <path d="M12 1.5l1.95 1.95 2.74-.5.5 2.74L19.14 7.64l-1.95 1.95.5 2.74-2.74.5L12 14.78l-1.95-1.95-2.74.5-.5-2.74L4.86 9.59l1.95-1.95-.5-2.74 2.74-.5L12 1.5z" fill="#0095f6"/>
  <path d="M15.6 5.7l-5 5-2.2-2.2" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
</svg>`;

// (LINK_ICON_SVG removed — bio link feature dropped since visitors are
// already on the page via the IG bio link.)

export const instagramBio: BlockDefinition<InstagramBioProps> = {
  type: 'instagram-bio',
  meta: {
    label: 'Instagram bio',
    icon: '📸',
    category: 'profile',
    description: 'Mimics the Instagram profile header — avatar, stats, bio, link.',
  },
  defaults: () => ({
    avatar: '',
    avatarInitials: 'Y',
    avatarSize: 'md',
    avatarCropX: 50,
    avatarCropY: 50,
    storyRing: true,
    username: 'yourhandle',
    verified: false,
    displayName: 'Your Name',
    posts: '124',
    followers: '12.4K',
    following: '386',
    category: 'Creator',
    bio: 'One line about you. Drop a tagline here.',
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<InstagramBioProps>;
    const clamp01to100 = (v: unknown, fallback: number): number => {
      if (typeof v !== 'number' || Number.isNaN(v)) return fallback;
      return Math.max(0, Math.min(100, v));
    };
    return {
      avatar: typeof i.avatar === 'string' ? i.avatar.slice(0, 2000) : '',
      avatarInitials: typeof i.avatarInitials === 'string' ? i.avatarInitials.slice(0, 3) : '',
      avatarSize: ['sm', 'md', 'lg'].includes(i.avatarSize as string)
        ? (i.avatarSize as InstagramBioProps['avatarSize'])
        : 'md',
      avatarCropX: clamp01to100(i.avatarCropX, 50),
      avatarCropY: clamp01to100(i.avatarCropY, 50),
      storyRing: i.storyRing !== false,
      username: typeof i.username === 'string' ? i.username.replace(/^@/, '').slice(0, 60) : '',
      verified: !!i.verified,
      displayName: typeof i.displayName === 'string' ? i.displayName.slice(0, 80) : '',
      posts: typeof i.posts === 'string' ? i.posts.slice(0, 16) : '',
      followers: typeof i.followers === 'string' ? i.followers.slice(0, 16) : '',
      following: typeof i.following === 'string' ? i.following.slice(0, 16) : '',
      category: typeof i.category === 'string' ? i.category.slice(0, 60) : '',
      bio: typeof i.bio === 'string' ? i.bio.slice(0, 600) : '',
    };
  },
  render(props) {
    // Avatar size — same sm/md/lg vocabulary as the standalone avatar
    // block. Default md (86px) reads well inline alongside the stats.
    const avatarPx = props.avatarSize === 'sm' ? 72 : props.avatarSize === 'lg' ? 108 : 86;
    const cropX = typeof props.avatarCropX === 'number' ? props.avatarCropX : 50;
    const cropY = typeof props.avatarCropY === 'number' ? props.avatarCropY : 50;
    let avatarInner: string;
    if (props.avatar) {
      avatarInner = `<img src="${safeUrl(props.avatar)}" alt="" style="display:block;width:${avatarPx}px;height:${avatarPx}px;border-radius:9999px;object-fit:cover;object-position:${cropX}% ${cropY}%;" loading="lazy" decoding="async" />`;
    } else {
      const init = escapeHtml(props.avatarInitials || '?').toUpperCase();
      avatarInner = `<div style="width:${avatarPx}px;height:${avatarPx}px;border-radius:9999px;background:rgba(0,0,0,0.06);display:flex;align-items:center;justify-content:center;font-size:${Math.floor(avatarPx * 0.35)}px;font-weight:600;color:var(--text);">${init}</div>`;
    }
    const avatarBlock = props.storyRing
      ? `<div style="padding:3px;background:${STORY_RING_GRADIENT};border-radius:9999px;flex-shrink:0;">
  <div style="padding:3px;background:var(--bg);border-radius:9999px;">
    ${avatarInner}
  </div>
</div>`
      : `<div style="flex-shrink:0;">${avatarInner}</div>`;

    // Username row — inline verified follows directly after, with a small
    // gap, matching IG's spacing. white-space:nowrap + overflow handles
    // very long handles without breaking the layout.
    const verifiedBadge = props.verified
      ? `<span style="display:inline-flex;align-items:center;">${INLINE_VERIFIED_SVG}</span>`
      : '';

    // Stats — three columns, each a stacked "value over label" cell.
    // Skipped when both value AND label would be empty, so a customer
    // who only fills two stats gets a clean two-column layout.
    const statCell = (value: string | undefined, label: string): string => {
      if (!value) return '';
      return `<div style="text-align:center;min-width:0;">
  <div style="font-size:15px;font-weight:700;color:var(--text);line-height:1.1;">${escapeHtml(value)}</div>
  <div style="font-size:11px;color:var(--text);opacity:0.65;margin-top:2px;">${label}</div>
</div>`;
    };
    const statsHtml = `<div style="display:flex;gap:18px;justify-content:flex-start;margin-top:8px;flex-wrap:wrap;">
  ${statCell(props.posts, 'posts')}
  ${statCell(props.followers, 'followers')}
  ${statCell(props.following, 'following')}
</div>`;

    // Bio body — display name (bold), category (muted), multi-line bio,
    // optional link with the link icon.
    const displayNameHtml = props.displayName
      ? `<div style="font-size:14px;font-weight:600;color:var(--text);line-height:1.3;">${escapeHtml(props.displayName)}</div>`
      : '';
    const categoryHtml = props.category
      ? `<div style="font-size:12px;color:var(--text);opacity:0.6;line-height:1.3;margin-top:2px;">${escapeHtml(props.category)}</div>`
      : '';
    const bioHtml = props.bio
      ? `<div style="font-size:14px;color:var(--text);line-height:1.45;margin-top:6px;white-space:pre-line;">${escapeHtml(props.bio)}</div>`
      : '';
    // (Bio link removed — visitors got HERE by tapping the bio link, so
    // surfacing another one is redundant.)

    const displayUsername = props.username ? `@${escapeHtml(props.username)}` : '@username';

    return `<div class="pb-block pb-ig-bio">
  <!-- Top row: avatar + (username + stats) -->
  <div style="display:flex;gap:18px;align-items:center;">
    ${avatarBlock}
    <div style="flex:1;min-width:0;">
      <div style="display:flex;align-items:center;gap:6px;font-size:16px;font-weight:600;color:var(--text);min-width:0;">
        <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${displayUsername}</span>
        ${verifiedBadge}
      </div>
      ${statsHtml}
    </div>
  </div>

  <!-- Bio block -->
  <div style="margin-top:12px;">
    ${displayNameHtml}
    ${categoryHtml}
    ${bioHtml}
  </div>
</div>`;
  },
};
